import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiBell, FiCalendar, FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";

interface OrderItem {
  orderNumber: string;
  agentName: string;
  orderId: string;
  orderType: string;
  agentId: string;
  customerName: string;
  totalItems: number;
  orderStatus: string;
  totalAmount: number;
}


interface OrderDetailProduct {
  name: string;
  measurement: string;
  image: string;
  type: string;
  quantity: number;
  totalPrice: number;
  productId: string;
  _id: string;
}

interface OrderDetail {
  agent: { id: string; agentName: string; email: string; phoneNumber: string | null };
  customer: { name: string; email: string; phone: string };
  order: {
    orderId: string;
    orderNumber: string;
    orderType: string;
    status: string;
    totalItems: number;
    totalAmount: number;
    items: OrderDetailProduct[];
    createdAt: string;
    updatedAt: string;
    completedAt: string;
  };
  payment: {
    method: string;
    details: Record<string, any>;
  };
}

type StatusFilter = "all" | "delivered" | "pending" | "cancelled";

export default function OrderManagement() {
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const ORDERS_PER_PAGE = 10;

  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 2 }).format(amount);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/orders/processed?limit=100`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setOrders(res.data.data.data ?? []);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch orders");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  // Client-side filter + search
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (statusFilter !== "all") {
      result = result.filter((o) => o.orderStatus.toLowerCase() === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (o) =>
          o.orderNumber.toLowerCase().includes(q) ||
          o.customerName.toLowerCase().includes(q) ||
          o.agentName.toLowerCase().includes(q)
      );
    }
    return result;
  }, [orders, statusFilter, searchQuery]);

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ORDERS_PER_PAGE,
    currentPage * ORDERS_PER_PAGE
  );

  const handleViewDetails = async (order: OrderItem) => {
    setSelectedOrder(order);
    setOrderDetail(null);
    setDetailLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/orders/processed/${order.orderId}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setOrderDetail(res.data.data);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch order details");
    } finally {
      setDetailLoading(false);
    }
  };


  const statusBadge = (status: string) => {
    const s = status.toLowerCase();
    switch (s) {
      case "delivered":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#EAF9F2] text-[#008060] rounded-full text-[11px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#008060]" />
            Delivered
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-600 rounded-full text-[11px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Pending
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-600 rounded-full text-[11px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-[11px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
            {status}
          </span>
        );
    }
  };

  const orderTypeBadge = (type: string) => {
    const t = type.toLowerCase();
    if (t === "walk-in") {
      return <span className="text-xs font-bold text-emerald-600">Walk-In</span>;
    }
    if (t === "pickup") {
      return <span className="text-xs font-bold text-orange-500">Pick-Up</span>;
    }
    return <span className="text-xs font-bold text-gray-500 capitalize">{type}</span>;
  };

  const STATUS_TABS: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "All Orders" },
    { value: "delivered", label: "Delivered" },
    { value: "pending", label: "Pending" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans relative">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950 tracking-tight">Order Queue Management</h1>
          <p className="text-gray-400 text-sm mt-0.5">Live fulfilment queue — all statuses</p>
        </div>

        <div className="flex items-center gap-3 self-stretch md:self-auto flex-wrap">
          <WalkInAlertsPill />

          <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-100 shadow-sm text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors">
            <FiCalendar className="w-4 h-4 text-[#008060]" />
            <span>
              {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })},{" "}
              {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </button>

          <button className="flex items-center justify-between gap-3 px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm min-w-[150px]">
            <span>Export Summary</span>
            <FiChevronDown className="text-white" />
          </button>
        </div>
      </div>

      {/* Search + Status Tabs Row */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        {/* Search Input */}
        <div className="relative flex-1 w-full md:max-w-md">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by order number, customer, or agent…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-[#008060] transition-all shadow-sm placeholder-gray-400"
          />
        </div>

        {/* Status Tabs */}
        <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100 gap-0.5">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                statusFilter === tab.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-400 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-[#EAF5EF] text-[#008060] text-xs font-bold rounded-lg">
                <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Order</th>
                <th className="p-3.5 font-bold tracking-wide">Customer</th>
                <th className="p-3.5 font-bold tracking-wide">Items</th>
                <th className="p-3.5 font-bold tracking-wide">Total Amount</th>
                <th className="p-3.5 font-bold tracking-wide">Order Type</th>
                <th className="p-3.5 font-bold tracking-wide">Agent</th>
                <th className="p-3.5 font-bold tracking-wide">Order Status</th>
                <th className="p-3.5 pr-4 rounded-r-lg font-bold tracking-wide">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="p-3.5">
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => (
                  <tr key={order.orderId} className="hover:bg-gray-50/30 transition-colors">
                    <td className="p-3.5 pl-4 text-[#008060] font-bold text-xs">{order.orderNumber}</td>
                    <td className="p-3.5 text-gray-900 font-semibold">{order.customerName || "—"}</td>
                    <td className="p-3.5 text-gray-900">{order.totalItems}</td>
                    <td className="p-3.5 text-gray-900 font-bold">{formatCurrency(order.totalAmount)}</td>
                    <td className="p-3.5">{orderTypeBadge(order.orderType)}</td>
                    <td className="p-3.5 text-gray-700">{order.agentName}</td>
                    <td className="p-3.5">{statusBadge(order.orderStatus)}</td>
                    <td className="p-3.5 pr-4">
                      <button
                        onClick={() => handleViewDetails(order)}
                        className="text-blue-600 hover:text-blue-800 transition-colors text-xs font-bold"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-gray-400">
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && (
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="text-xs font-semibold text-gray-600 disabled:text-gray-300 transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-gray-400">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="text-xs font-semibold text-[#008060] disabled:text-gray-300 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/45 flex items-center justify-center p-4 z-50 animate-fadeIn overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto border border-gray-100/50 p-6 flex flex-col gap-6 relative scrollbar-thin">
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-gray-50 pb-4">
              <div className="flex items-center gap-3">
                <span className="text-[#008060] text-lg font-black">{selectedOrder.orderNumber}</span>
                {statusBadge(orderDetail?.order?.status ?? selectedOrder.orderStatus)}
              </div>
              <button
                onClick={() => { setSelectedOrder(null); setOrderDetail(null); }}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]" />
              </div>
            ) : orderDetail ? (
              <>
                {/* Customer & Payment Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Customer</h4>
                    <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex flex-col gap-2.5 h-full">
                      <p className="text-xs text-gray-500 font-semibold">
                        Name: <span className="text-gray-800 font-bold ml-1">{orderDetail.customer.name || "Walk-in Customer"}</span>
                      </p>
                      {orderDetail.customer.email && (
                        <p className="text-xs text-gray-500 font-semibold">
                          Email: <span className="text-gray-800 font-semibold ml-1">{orderDetail.customer.email}</span>
                        </p>
                      )}
                      {orderDetail.customer.phone && (
                        <p className="text-xs text-gray-500 font-semibold">
                          Phone: <span className="text-gray-800 font-bold ml-1">{orderDetail.customer.phone}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Payment</h4>
                    <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex flex-col gap-2.5 h-full">
                      <p className="text-xs text-gray-500 font-semibold">
                        Method: <span className="text-gray-800 font-bold ml-1 capitalize">{orderDetail.payment.method}</span>
                      </p>
                      {orderDetail.payment.details?.cash && (
                        <>
                          <p className="text-xs text-gray-500 font-semibold">
                            Tendered: <span className="text-gray-800 font-bold ml-1">{formatCurrency(orderDetail.payment.details.cash.amountTendered)}</span>
                          </p>
                          <p className="text-xs text-gray-500 font-semibold">
                            Change: <span className="text-amber-600 font-bold ml-1">{formatCurrency(orderDetail.payment.details.cash.changeDue)}</span>
                          </p>
                        </>
                      )}
                      <p className="text-xs text-gray-500 font-semibold">
                        Total: <span className="text-[#008060] font-black ml-1">{formatCurrency(orderDetail.order.totalAmount)}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Agent */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Agent</h4>
                  <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#EAF9F2] text-[#008060] font-black text-xs flex items-center justify-center select-none shadow-sm">
                      {orderDetail.agent.agentName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{orderDetail.agent.agentName}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{orderDetail.agent.email}</p>
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Products</h4>
                  <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-gray-400 text-[10px] font-bold">
                          <th className="py-2.5 px-4 font-bold tracking-wide">PRODUCT</th>
                          <th className="py-2.5 px-3 font-bold tracking-wide text-center">QTY</th>
                          <th className="py-2.5 px-4 text-right font-bold tracking-wide">PRICE</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 text-gray-700 font-medium">
                        {orderDetail.order.items.map((item) => (
                          <tr key={item._id} className="hover:bg-gray-50/20 transition-colors">
                            <td className="py-3 px-4 flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl border border-gray-100 overflow-hidden bg-gray-50 shrink-0">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = ""; (e.target as HTMLImageElement).style.display = "none"; }} />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{item.name}</p>
                                <p className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">{item.type} · {item.measurement}</p>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center text-gray-900 font-bold">{item.quantity}</td>
                            <td className="py-3 px-4 text-right text-gray-900 font-bold">{formatCurrency(item.totalPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="flex flex-wrap gap-4 text-[10px] text-gray-400 font-medium">
                  <span>Created: {new Date(orderDetail.order.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  {orderDetail.order.completedAt && (
                    <span>Completed: {new Date(orderDetail.order.completedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  )}
                </div>

                {/* Summary Row */}
                <div className="flex justify-between items-center border-t border-gray-100 pt-4">
                  <span className="text-sm text-gray-500 font-semibold">Total Items: <span className="text-gray-900 font-bold">{orderDetail.order.totalItems}</span></span>
                  <span className="text-lg font-black text-gray-950">{formatCurrency(orderDetail.order.totalAmount)}</span>
                </div>
              </>
            ) : (
              <div className="text-center py-10 text-gray-400 text-sm">Failed to load order details.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
